import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { KTIcon, KTCard } from '../../../../../../_metronic/helpers'
import WCC from '../form-edit-modal/WCC'
import 'react-datepicker/dist/react-datepicker.css'

interface ApiData {
  overallTotalDue: number
  overallTotalPaid: number
  overallTotalBalance: number
  overallTotalSupplied: number
  userRewardDetails: User[]
}

interface ApiResponse {
  data: ApiData
  meta?: {
    pageSize: number
    currentPage: number
  }
  totalStock?: number
}

interface User {
  userId: string
  userProfile?: string
  userName: string
  totalDue: number
  totalBalance: number
  totalPaid: number
  totalSupplied: number
  transactions?: any[]
}

const ReceivableFacility = () => {
  const [users, setUsers] = useState<ApiData>({
    overallTotalDue: 0,
    overallTotalPaid: 0,
    overallTotalBalance: 0,
    overallTotalSupplied: 0,
    userRewardDetails: []
  })
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [showModal, setShowModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  useEffect(() => {
    if (searchQuery === '') {
      setFilteredUsers(users.userRewardDetails)
    } else {
      const filtered = users.userRewardDetails.filter(user =>
        user.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.userId?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredUsers(filtered)
    }
  }, [searchQuery, users])

  const fetchData = async () => {
    setError('')

    try {
      const authData = JSON.parse(localStorage.getItem('kt-auth-react-v') || '{}')
      const token = authData.data?.token
      const pickuppointid = authData.data?.pickuppointid

      if (!token || !pickuppointid) {
        setError('Authentication data not found')
        return
      }

      const response = await axios.get<ApiResponse>(
        `${process.env.REACT_APP_BASE_API_URL}user/due/b2b?direction=from&page=1&size=100`,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': token,
            'app-id': '3',
            'client-id': '101212',
            'pickupPointId': pickuppointid
          }
        }
      )

      const userData = response.data?.data || response.data || {}
      const safeUserData: ApiData = {
        overallTotalDue: userData.overallTotalDue || 0,
        overallTotalPaid: userData.overallTotalPaid || 0,
        overallTotalBalance: userData.overallTotalBalance || 0,
        overallTotalSupplied: userData.overallTotalSupplied || 0,
        userRewardDetails: Array.isArray(userData.userRewardDetails) ? userData.userRewardDetails : []
      }

      setUsers(safeUserData)
      setFilteredUsers(safeUserData.userRewardDetails)
    } catch (err) {
      setError('Failed to fetch data')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault()
    fetchData()
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleEditClick = (user: User) => {
    setSelectedUser(user)
    setShowModal(true)
  }

  return (
    <KTCard>
      <div style={{ padding: '20px' }}>
        {/* Search Box */}
        <div style={{ marginBottom: '20px', position: 'relative', width: '300px' }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6B7280',
            }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={handleSearch}
            style={{
              width: '100%',
              height: '40px',
              padding: '8px 8px 8px 35px',
              fontSize: '14px',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              outline: 'none',
              backgroundColor: 'white',
            }}
          />
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
          {[
            { title: 'Material sold', value: users.overallTotalSupplied, bg: '#dff5eb', icon: '/media/svg/dashboard/New/recycling capacity.png' },
            { title: 'Total value', value: users.overallTotalDue, bg: '#c8f0e8', icon: '/media/svg/dashboard/New/total value.png' },
            { title: 'Total payment', value: users.overallTotalPaid, bg: '#e4d9f2', icon: '/media/svg/dashboard/New/Total paid.png' },
            { title: 'Closing balance', value: users.overallTotalBalance, bg: '#f7f5c5', icon: '/media/svg/dashboard/New/Pending Payment.png' }
          ].map((card, index) => (
            <div key={index} style={{
              flex: 1,
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              backgroundColor: '#ffffff',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: card.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px'
              }}>
                <img src={card.icon} alt="" style={{ width: '27px', height: '27px' }} />
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '400' }}>{card.title}</div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>
                  {card.value.toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#333333', color: 'white' }}>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>S.N</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Profile</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Supplied qty</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Value</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Paid</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Payment due</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => (
                  <tr key={user.userId} style={{ backgroundColor: 'white' }}>
                    <td style={{ padding: '12px' }}>{index + 1}</td>
                    <td style={{ padding: '12px' }}>
                      {user.userProfile ? (
                        <img
                          src={user.userProfile}
                          alt="Profile"
                          style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const fallback = target.nextElementSibling as HTMLDivElement
                            if (fallback) fallback.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <div
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          backgroundColor: '#333333',
                          display: user.userProfile ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '15px',
                          color: 'white'
                        }}
                      >
                        {user.userName ? user.userName.substring(0, 2).toUpperCase() : 'NA'}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {user.userName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {(user.totalSupplied || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {(user.totalDue || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '5px 8px',
                        fontWeight: '600',
                        color: '#17c653',
                        backgroundColor: '#dfffea',
                        borderRadius: '8px'
                      }}>
                        {(user.totalPaid || 0).toLocaleString()}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '5px 8px',
                        fontWeight: '600',
                        color: '#f8285a',
                        backgroundColor: '#ffeef3',
                        borderRadius: '8px'
                      }}>
                        {(user.totalBalance || 0).toLocaleString()}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ cursor: 'pointer' }} onClick={() => handleEditClick(user)}>
                        <KTIcon iconName='pencil' className='fs-3' />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#9ca3af',
                      fontSize: '13px'
                    }}
                  >
                    {!filteredUsers.length ? (
                      " No records found"
                    ) : null}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Edit Modal */}
        {showModal && selectedUser && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setShowModal(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                maxWidth: '800px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>Edit user</h3>
                <button
                  onClick={() => setShowModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
              <WCC
                isUserLoading={false}
                user={selectedUser}
                onClose={() => setShowModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </KTCard>
  )
}

export { ReceivableFacility }